import { IProduct } from '@homeserve/od-typings/api';
import { Nullable } from '@homeserve/od-typings/utils';
import { IVoucher } from '@homeserve/vue-api-plugin';
import { PriceOption, TimeSlot } from '../components/WLFixAppointment/interfaces';

export interface IProductPriceDetail {
  calloutPrice: number;
  extraChargeAmount: number;
  priceIncVat: number;
  priceIncVatWithoutDiscount: number;
  sparePartsCost: number;
  taxFreePrice: number;
  vatAmount: number;
  vatRate: number;
  workForcePrice: number;
  discount: number;
  totalPrice: number;
}

interface IProductPriceDetailParams {
  product: IProduct;
  slot: Nullable<TimeSlot>;
  housingOlderThan2Years?: boolean;
  voucher?: IVoucher;
  customerSupportDiscountAmount?: number;
}

export function calculateVatInc(price: number, vatRate: number, voucher?: IVoucher) {
  const vat = price * (vatRate / 100);
  const priceIncVat = price + vat;
  const result = {
    vatAmount: vat,
    priceIncVat,
    discount: 0,
    priceIncVatWithoutDiscount: priceIncVat,
  };
  if (voucher) {
    result.discount = calculateDiscount(voucher, result.priceIncVat);
    result.priceIncVat = result.priceIncVat - result.discount;
  }
  return result;
}

export function calculateDiscount(voucher: IVoucher, totalPrice: number) {
  return voucher.type === 'percent' ? totalPrice * (voucher.amount / 100) : voucher.amount;
}

export function getProductPriceDetail(params: IProductPriceDetailParams): IProductPriceDetail {
  const { product, slot, housingOlderThan2Years = true, voucher, customerSupportDiscountAmount } = params;
  const productPriceDetails: IProductPriceDetail = {
    extraChargeAmount: 0,
    priceIncVat: 0,
    priceIncVatWithoutDiscount: 0,
    taxFreePrice: 0,
    vatAmount: 0,
    vatRate: 0,
    sparePartsCost: 0,
    calloutPrice: 0,
    workForcePrice: 0,
    discount: 0,
    totalPrice: 0,
  };

  if (!product || !slot) {
    return productPriceDetails;
  }

  productPriceDetails.vatRate = product.isVatRateHouseAgeDependant
    ? housingOlderThan2Years
      ? product.customerVatRate
      : 20
    : product.customerVatRate;

  if (slot.cost === PriceOption.customerExtraChargeNights) {
    productPriceDetails.taxFreePrice = product.customerBasePurchasePriceNightsTaxFree;
    productPriceDetails.extraChargeAmount = product.customerExtraChargeNightsTaxFree;
  } else if (slot.cost === PriceOption.customerExtraChargeSundays) {
    productPriceDetails.taxFreePrice = product.customerBasePurchasePriceSundaysTaxFree;
    productPriceDetails.extraChargeAmount = product.customerExtraChargeSundaysTaxFree;
  } else {
    productPriceDetails.taxFreePrice = product.customerBasePurchasePriceTaxFree;
    productPriceDetails.extraChargeAmount = 0;
  }

  const { priceIncVat, vatAmount, discount, priceIncVatWithoutDiscount } = calculateVatInc(
    productPriceDetails.taxFreePrice,
    productPriceDetails.vatRate,
    voucher,
  );
  productPriceDetails.priceIncVatWithoutDiscount = priceIncVatWithoutDiscount;
  productPriceDetails.priceIncVat = priceIncVat;
  productPriceDetails.vatAmount = vatAmount;
  productPriceDetails.discount = discount;

  productPriceDetails.extraChargeAmount = calculateVatInc(
    productPriceDetails.extraChargeAmount,
    productPriceDetails.vatRate,
  ).priceIncVat;

  productPriceDetails.calloutPrice = calculateVatInc(
    product.customerCallOutPriceTaxFree,
    productPriceDetails.vatRate,
  ).priceIncVat;

  productPriceDetails.workForcePrice = calculateVatInc(
    product.customerJobWorkforceTaxFree,
    productPriceDetails.vatRate,
  ).priceIncVat;

  productPriceDetails.sparePartsCost = calculateVatInc(
    product.customerSparePartsCostTaxFree,
    productPriceDetails.vatRate,
  ).priceIncVat;

  productPriceDetails.totalPrice =
    typeof customerSupportDiscountAmount === 'number' && !Number.isNaN(customerSupportDiscountAmount)
      ? productPriceDetails.priceIncVat - customerSupportDiscountAmount
      : productPriceDetails.priceIncVat;

  return productPriceDetails;
}
